import { debounce } from "lodash-es";
import { useSyncExternalStore } from "react";

const BREAKPOINTS = {
  xs: 640,
  sm: 768,
  md: 1024,
  lg: 1280,
  xl: 1526,
} as const;

type Breakpoint = keyof typeof BREAKPOINTS;

export type UseScreenData<TData> = {
  base: TData;
  xs?: TData;
  sm?: TData;
  md?: TData;
  lg?: TData;
  xl?: TData;
  "2xl"?: TData;
};

function getMediaQuery(bp: Breakpoint) {
  return `(min-width: ${BREAKPOINTS[bp]}px)`;
}

function subscribe(callback: () => void) {
  const mqs = Object.values(BREAKPOINTS).map((bp) =>
    window.matchMedia(`(min-width: ${bp}px)`),
  );

  const debounced = debounce(callback, 200);

  mqs.forEach((mq) => mq.addEventListener("change", debounced));

  return () => {
    mqs.forEach((mq) => mq.removeEventListener("change", debounced));
  };
}

function getSnapshot() {
  if (typeof window === "undefined") return 0;
  return window.innerWidth;
}

// SSR snapshot
function getServerSnapshot() {
  return 0;
}

export function useScreen<TData>(data: UseScreenData<TData>) {
  const width = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const getBreakpointValue = (width: number): TData => {
    let currentValue = data.base;

    const breakpoints: Breakpoint[] = ["xs", "sm", "md", "lg", "xl"];

    for (const bp of breakpoints) {
      currentValue = data[bp] ?? currentValue;
      if (width < BREAKPOINTS[bp]) return currentValue;
    }

    return data["2xl"] ?? currentValue;
  };

  return getBreakpointValue(width);
}
